import { useEffect, useRef, useState } from 'react'
import AddTraspButton from '../../firstTimeUserExperience/commonCom/AddTraspButton';
import { GetImages } from '../../../utils/GetImages';
import CustomInputs from '../Inputs';
import { Form } from 'react-bootstrap';

const EditorToolBar = ({ editorRef, handleFileChange, disableAttachments = false }) => {
    const [activeCommand, setActiveCommand] = useState<string | null>(null);
    const savedRangeRef = useRef<Range | null>(null);

    const fontSizes = Array.from({ length: 14 }, (_, i) => i + 1); // Sizes 1 to 14

    // Caches the editor's live selection Range on mouseup/keyup -- clicking the font-size <select>
    // below can clear window.getSelection() before onChange fires, so we need our own last-known
    // selection rather than reading it fresh at that point.
    useEffect(() => {
        const editor = editorRef?.current;
        if (!editor) return;
        const saveSelection = () => {
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0 && editor.contains(selection.anchorNode)) {
                savedRangeRef.current = selection.getRangeAt(0).cloneRange();
            }
        };
        editor.addEventListener("mouseup", saveSelection);
        editor.addEventListener("keyup", saveSelection);
        return () => {
            editor.removeEventListener("mouseup", saveSelection);
            editor.removeEventListener("keyup", saveSelection);
        };
    }, [editorRef]);

    const applyCommand = (command, value = null) => {
        if (activeCommand === command) {
            setActiveCommand(null);
        } else {
            setActiveCommand(command);
        }
        document.execCommand(command, false, value);
    };

    // document.execCommand("fontSize", ...) only recognizes the legacy HTML font-size levels 1-7
    // (<font size="N">) -- this dropdown goes to 14, so anything above 7 is silently clamped to
    // level 7 by the browser, and re-applying a *smaller* level after a larger one is a well-known
    // cross-browser unreliability of this deprecated command.
    //
    // A hand-rolled range.extractContents()/insertNode() replacement corrupts the editor's block
    // structure whenever the selection spans multiple lines or list items (contentEditable creates
    // one <div>/<li> per line, and extractContents() clones each partially-selected block rather
    // than merging them, so e.g. a two-line selection becomes four lines). Instead, let the browser
    // itself handle the (hard) job of correctly splitting/wrapping a multi-node selection via
    // execCommand at a single legacy level (7), then re-tag only the <font> elements THIS call just
    // created (diffed against what already existed) with our own literal pixel size.
    const FONT_SIZE_PX_SCALE = 3;
    const FONT_SIZE_PX_BASE = 8; // dropdown values 1-14 map to 11px-50px, a visibly distinct range
    const changeFontSize = (e) => {
        const size = Number(e.target.value);
        const editor = editorRef?.current;
        if (!size || !editor) return;
        const selection = window.getSelection();
        if (!selection) return;
        if (savedRangeRef.current && editor.contains(savedRangeRef.current.commonAncestorContainer)) {
            selection.removeAllRanges();
            selection.addRange(savedRangeRef.current);
        }
        if (selection.rangeCount === 0 || selection.getRangeAt(0).collapsed) return;
        const range = selection.getRangeAt(0);
        if (!editor.contains(range.commonAncestorContainer)) return;

        const preExistingLevel7Fonts = new Set(editor.querySelectorAll('font[size="7"]'));
        document.execCommand("fontSize", false, "7");

        const pxSize = size * FONT_SIZE_PX_SCALE + FONT_SIZE_PX_BASE;
        editor.querySelectorAll('font[size="7"]').forEach((fontEl) => {
            if (preExistingLevel7Fonts.has(fontEl)) return;
            const span = document.createElement("span");
            span.style.fontSize = `${pxSize}px`;
            while (fontEl.firstChild) span.appendChild(fontEl.firstChild);
            fontEl.replaceWith(span);
        });

        // execCommand doesn't fire a React-visible "input" event -- controlled forms that track
        // dirty state off the editor's own input handler (e.g. the Maintenance Banner drawer) need
        // this nudge or Save stays disabled after a font-size-only edit.
        editor.dispatchEvent(new Event("input", { bubbles: true }));

        if (selection.rangeCount > 0) {
            savedRangeRef.current = selection.getRangeAt(0).cloneRange();
        }
    };

    const handleInsertImage = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            if (editorRef.current) {
                const img = document.createElement("img");
                img.src = e.target.result as string;
                img.alt = file.name;
                img.style.maxWidth = "100%";
                img.style.margin = "10px 0";

                editorRef.current.appendChild(img);
            }
        };
        reader.readAsDataURL(file);
    };

    const onFileChange = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleInsertImage(files[0]);
        }
    };


    return (
        <div className="Editor_toolbar">
            <select className="toolbar-select" onChange={changeFontSize}>
                {fontSizes.map((size) => (
                    <option key={size} value={size}>
                        {size}
                    </option>
                ))}
            </select>
            <div className="divider"></div>
            <input
                type="color"
                className="toolbar-color-picker"
                onChange={(e) => applyCommand("foreColor", e.target.value)}
            />
            <div className="divider"></div>
            <AddTraspButton
                type={"button"}
                width={0}
                bname={<b>B</b>}
                textclass={activeCommand === "bold" ? "sub_heading grey fw-600" : "sub_heading grey fw-600"}
                className="px-1 toolbar-button"
                onClick={() => applyCommand("bold")}
                border={false}
            />
            <AddTraspButton
                type={"button"}
                width={0}
                bname={<i>I</i>}
                textclass={activeCommand === "italic" ? "sub_heading grey fw-600" : "sub_heading grey fw-600"}
                className="px-1 toolbar-button"
                onClick={() => applyCommand("italic")}
                border={false}
            />
            <AddTraspButton
                type={"button"}
                width={0}
                bname={<s>S</s>}
                textclass={activeCommand === "strikeThrough" ? "sub_heading grey fw-600" : "sub_heading grey fw-600"}
                className="p-0"
                onClick={() => applyCommand("strikeThrough")}
                border={false}
            />
            <AddTraspButton
                type={"button"}
                width={0}
                bname={<u>U</u>}
                className="p-0"
                textclass={activeCommand === "underline" ? "sub_heading grey fw-600" : "sub_heading grey fw-600"}
                onClick={() => applyCommand("underline")}
                border={false}
            />
            <div className="divider"></div>
            <AddTraspButton
                type={"button"}
                width={0}
                bname={undefined}
                className="p-0"
                imageValue={GetImages.GreyLeftAlign}
                iconPosition={"right"}
                onClick={() => applyCommand("justifyLeft")}
                border={false}
            />
            <AddTraspButton
                type={"button"}
                width={0}
                bname={undefined}
                className="p-0"
                imageValue={GetImages.GreyCenterAlign}
                iconPosition={"right"}
                onClick={() => applyCommand("justifyCenter")}
                border={false}
            />
            <AddTraspButton
                type={"button"}
                width={0}
                bname={undefined}
                className="p-0"
                imageValue={GetImages.GreyRightAlign}
                iconPosition={"right"}
                onClick={() => applyCommand("justifyRight")}
                border={false}
            />
            <div className="divider"></div>
            <AddTraspButton
                type={"button"}
                width={0}
                bname={undefined}
                className="p-0"
                imageValue={GetImages.GreyNumberList}
                iconPosition={"right"}
                onClick={() => applyCommand("insertOrderedList")}
                border={false}
            />
            <AddTraspButton
                type={"button"}
                width={0}
                bname={undefined}
                className="p-0"
                imageValue={GetImages.GreyDotList}
                iconPosition={"right"}
                onClick={() => applyCommand("insertUnorderedList")}
                border={false}
            />
            <div className="divider"></div>
            <AddTraspButton
                type={"button"}
                width={0}
                bname={undefined}
                className="p-0"
                imageValue={GetImages.GreyLink}
                iconPosition={"right"}
                onClick={()=> applyCommand("createLink", window.getSelection()?.toString())}
                border={false}
            />
            <AddTraspButton
                type={"button"}
                width={0}
                bname={"X"}
                className="p-0"
                imageValue={undefined}
                iconPosition={"right"}
                onClick={() => applyCommand("unlink")}
                textclass={activeCommand === "unlink" ? "sub_heading grey fw-600" : "sub_heading grey fw-600"}
                border={false}
            />
            <Form.Group controlId="formFile" className="mb-0">
                <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={onFileChange}
                    style={{ display: "none" }}
                />
                <AddTraspButton
                    type={"button"}
                    width={0}
                    bname={undefined}
                    className="p-0"
                    imageValue={GetImages.GreyImageicon}
                    iconPosition={"right"}
                    onClick={() => document.getElementById("formFile").click()}
                    border={false}
                />
            </Form.Group>
            {!disableAttachments && (
                <>
                    <div className="divider"></div>
                    <div className="attachment-container">
                        <label htmlFor="file-input">
                            <img src={GetImages.BlackAttachemnet} className="iconstyle24" />
                            <CustomInputs
                                type="file"
                                id="file-input"
                                width={0}
                                multiple
                                onChange={handleFileChange}
                                myClassName="email-file-input"
                            />
                        </label>
                    </div>
                </>
            )}
        </div>
    )
}

export default EditorToolBar